import { useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@app/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@app/components/ui/tooltip';
import { cn } from '@app/lib/utils';
import invariant from '@promptfoo/util/invariant';

import { ChevronDown } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

type SplitButtonOptionBase = {
  label: string;
  labelSuffix?: React.ReactNode;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
};

type SplitButtonActionOption = SplitButtonOptionBase & {
  onClick: () => void;
  to?: never;
  href?: never;
};

type SplitButtonInternalLinkOption = SplitButtonOptionBase & {
  to: string;
  onClick?: never;
  href?: never;
};

type SplitButtonExternalLinkOption = SplitButtonOptionBase & {
  href: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  onClick?: never;
  to?: never;
};

/**
 * Configuration for a single option in a SplitButton dropdown.
 *
 * Three mutually exclusive variants are supported:
 * - **Action**: Use `onClick` for button behavior (e.g., opening a modal)
 * - **Internal Link**: Use `to` for React Router navigation within the app
 * - **External Link**: Use `href` for links to external URLs
 *
 * @example
 * // Action option
 * { label: 'Delete', onClick: () => handleDelete(), icon: <TrashIcon /> }
 *
 * // Internal link (supports right-click "open in new tab")
 * { label: 'View Details', to: '/items/123' }
 *
 * // External link (auto-adds rel="noopener noreferrer" for target="_blank")
 * { label: 'Documentation', href: 'https://docs.example.com', target: '_blank' }
 */
export type SplitButtonOption =
  | SplitButtonActionOption
  | SplitButtonInternalLinkOption
  | SplitButtonExternalLinkOption;

export type SplitButtonProps = {
  /**
   * The label to display on the button. If not provided, the selected option will be displayed.
   */
  label?: React.ReactNode;
  startIcon?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
  /** Visually hides the label on narrow screens when the leading icon is sufficient. */
  compactOnMobile?: boolean;
  /**
   * The options to display in the dropdown.
   */
  options: SplitButtonOption[];
  variant?: 'default' | 'outline';
  /**
   * The size of the button.
   */
  size?: 'sm' | 'default' | 'lg';
  /**
   * Whether the entire button group is disabled.
   */
  disabled?: boolean;
  /**
   * Horizontal alignment of the dropdown menu relative to the button.
   * - 'start': Align to the left edge of the button
   * - 'center': Center align with the button
   * - 'end': Align to the right edge of the button
   */
  dropdownAlign?: 'start' | 'center' | 'end';
  /**
   * Test ID for the button group container.
   */
  'data-testid'?: string;
};

const sizeClasses = {
  sm: 'h-8 px-2 text-xs',
  default: 'h-9 px-3 text-sm',
  lg: 'h-10 px-4 text-base',
};

const chevronSizeClasses = {
  sm: 'h-8 px-1.5',
  default: 'h-9 px-2',
  lg: 'h-10 px-2.5',
};

const chevronIconSizeClasses = {
  sm: 'size-4',
  default: 'size-5',
  lg: 'size-5',
};

/**
 * Generic split button component using Radix UI DropdownMenu.
 */
export default function SplitButton({
  label,
  startIcon,
  ref,
  compactOnMobile = false,
  options,
  variant = 'default',
  size = 'default',
  disabled = false,
  dropdownAlign = 'center',
  'data-testid': testId,
}: SplitButtonProps) {
  invariant(options.length > 0, 'options must be an array with at least one option');

  const [open, setOpen] = useState(false);

  const isOutline = variant === 'outline';
  const outlineBorderStyle = isOutline ? { borderColor: 'hsl(var(--input))' } : undefined;

  // Base styles shared by both buttons
  const baseClasses =
    'relative inline-flex items-center font-medium focus:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

  // Container styles with unified border
  const containerClasses = cn(
    'inline-flex cursor-pointer rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
    isOutline
      ? 'border border-input bg-background hover:bg-accent dark:border-white/20 dark:bg-white/10 dark:hover:bg-white/20'
      : 'bg-primary hover:bg-primary/90',
  );

  // Main button styles (with text)
  const mainButtonClasses = cn(
    baseClasses,
    isOutline
      ? 'text-foreground hover:bg-accent hover:text-accent-foreground dark:text-white dark:hover:bg-white/20'
      : 'text-primary-foreground hover:bg-primary/90',
  );

  // Chevron button styles (icon only, slightly muted in outline variant)
  const chevronButtonClasses = cn(
    baseClasses,
    isOutline
      ? 'border-l border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:border-white/20 dark:text-gray-400 dark:hover:bg-white/20'
      : 'border-l border-primary-foreground/20 text-primary-foreground hover:bg-primary/90',
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          ref={ref}
          className={containerClasses}
          data-testid={testId}
          disabled={disabled}
          aria-disabled={disabled}
          style={outlineBorderStyle}
        >
          <span
            className={cn(
              mainButtonClasses,
              sizeClasses[size],
              'justify-center gap-2 rounded-l-md',
              compactOnMobile && 'max-sm:gap-0 max-sm:px-2',
              disabled && 'pointer-events-none opacity-50',
            )}
          >
            {startIcon}
            <span className={cn(compactOnMobile && 'sr-only sm:not-sr-only')}>
              {label ?? options[0].label}
            </span>
          </span>
          <span
            className={cn(
              chevronButtonClasses,
              chevronSizeClasses[size],
              'justify-center rounded-r-md',
              compactOnMobile && 'max-sm:px-1',
              disabled && 'pointer-events-none opacity-50',
            )}
            aria-hidden="true"
            style={outlineBorderStyle}
          >
            <ChevronDown className={chevronIconSizeClasses[size]} aria-hidden="true" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={dropdownAlign} className="min-w-[180px]">
        {options.map((option, index) => {
          const optionDisabled = option.disabled ?? false;
          const optionTestId = testId ? `${testId}-option-${index}` : undefined;
          const optionClassName = cn(
            option.description && 'flex-col items-start gap-0.5 py-2',
            option.icon && !option.description && 'gap-2',
          );

          const optionContents = option.description ? (
            <>
              <span className="text-sm font-medium inline-flex items-center gap-1.5">
                {option.label}
                {option.labelSuffix}
              </span>
              <span className="text-xs text-muted-foreground">{option.description}</span>
            </>
          ) : (
            <>
              {option.icon}
              {option.label}
            </>
          );

          const isInternalLink = 'to' in option && option.to !== '';
          const isExternalLink = 'href' in option && option.href !== '';

          const menuItem =
            isInternalLink && !optionDisabled ? (
              <DropdownMenuItem
                key={index}
                asChild
                data-testid={optionTestId}
                className={optionClassName}
              >
                {/* @ts-expect-error TODO: fix TS2322: Type 'string | undefined' is not assignable to type 'To'. */}
                <RouterLink to={option.to}>{optionContents}</RouterLink>
              </DropdownMenuItem>
            ) : isExternalLink && !optionDisabled ? (
              <DropdownMenuItem
                key={index}
                asChild
                data-testid={optionTestId}
                className={optionClassName}
              >
                <a
                  href={option.href}
                  // @ts-expect-error TODO: fix TS2339: Property 'target' does not exist on type 'SplitButtonOption'.
                  target={option.target}
                  rel={
                    // @ts-expect-error TODO: fix TS2339: Property 'rel' does not exist on type 'SplitButtonOption'.
                    option.rel ?? (option.target === '_blank' ? 'noopener noreferrer' : undefined)
                  }
                >
                  {optionContents}
                </a>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                key={index}
                onClick={() => {
                  if (!('onClick' in option)) {
                    return;
                  }
                  // @ts-expect-error TODO: fix TS2722: Cannot invoke an object which is possibly 'undefined'.
                  option.onClick();
                  setOpen(false);
                }}
                disabled={optionDisabled}
                data-testid={optionTestId}
                className={optionClassName}
              >
                {optionContents}
              </DropdownMenuItem>
            );

          if (option.tooltip) {
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <span>{menuItem}</span>
                </TooltipTrigger>
                <TooltipContent>{option.tooltip}</TooltipContent>
              </Tooltip>
            );
          }

          return menuItem;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
